import { CreateNotePayload } from '@/types/notes';
import api, { API_PREFIX } from './api';

export const NOTES_STATE_KEY = 'notes';

/** Sidebar page size for org notes — keeps the admin org page from mounting tens of thousands of rows (KA-2044). */
export const ORGANIZATION_NOTES_PAGE_SIZE = 50;

/**
 * Hard cap on sidebar pages so "Load more" cannot reaccumulate pathological note volumes.
 * Full history remains on Organization History.
 */
export const ORGANIZATION_NOTES_MAX_SIDEBAR_PAGES = 5;

export const getOrganizationNotes = (orgId: string, params: { page?: number; size?: number } = {}) => {
  return api.get(`${API_PREFIX}/notes/organization`, {
    headers: {
      'x-organization-id': orgId,
    },
    params: {
      page: params.page ?? 1,
      size: params.size ?? ORGANIZATION_NOTES_PAGE_SIZE,
    },
  });
};

export const getRegistrationNotes = (orgId: string, registrationId: string) => {
  return api.get(`${API_PREFIX}/notes/registration/${registrationId}`, {
    headers: {
      'x-organization-id': orgId,
    },
  });
};

export const getFilingNotes = (orgId: string, filingId: string) => {
  return api.get(`${API_PREFIX}/notes/filing/${filingId}`, {
    headers: {
      'x-organization-id': orgId,
    },
  });
};

export const createNote = async (orgId: string, payload: CreateNotePayload) => {
  return api.post(`${API_PREFIX}/notes`, payload, {
    headers: {
      'x-organization-id': orgId,
    },
  });
};

export const updateNotePin = async (orgId: string, noteId: string, payload: { pinned: boolean }) => {
  return api.put(`${API_PREFIX}/notes/${noteId}/pin`, payload, {
    headers: {
      'x-organization-id': orgId,
    },
  });
};

export const updateNoteCascade = async (orgId: string, noteId: string, payload: { cascading: boolean }) => {
  return api.put(`${API_PREFIX}/notes/${noteId}/cascade`, payload, {
    headers: {
      'x-organization-id': orgId,
    },
  });
};

export const updateNoteContent = async (orgId: string, noteId: string, payload: { content: string }) => {
  return api.put(`${API_PREFIX}/notes/${noteId}`, payload, {
    headers: {
      'x-organization-id': orgId,
    },
  });
};

export const deleteNote = async (orgId: string, noteId: string) => {
  return api.delete(`${API_PREFIX}/notes/${noteId}`, {
    headers: {
      'x-organization-id': orgId,
    },
  });
};

export const getOrganizationHistory = (orgId: string, params: Record<string, any>) => {
  return api.get(`${API_PREFIX}/notes/organization/history`, {
    headers: {
      'x-organization-id': orgId,
    },
    params,
  });
};

export const getPartnerNotes = (partnerId: string) => {
  return api.get(`${API_PREFIX}/notes/partner/${partnerId}`);
};

export const getPartnerNotesHistory = (partnerId: string, params: Record<string, unknown>) => {
  return api.get(`${API_PREFIX}/notes/partner/${partnerId}/history`, {
    params,
  });
};

export const createPartnerNote = (partnerId: string, payload: { content: string; is_pinned?: boolean }) => {
  return api.post(`${API_PREFIX}/notes/partner`, {
    partner_id: partnerId,
    content: payload.content,
    is_pinned: payload.is_pinned ?? false,
  });
};

export const pinPartnerNote = (noteId: string, payload: { pinned: boolean }) => {
  return api.put(`${API_PREFIX}/notes/partner/${noteId}/pin`, payload);
};
